import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { clsx } from 'clsx';
import * as React from 'react';
/**
 * Derive initials from a name
 *
 * Rules:
 * - Two-word names: first letter of each word (e.g., "John Doe" → "JD")
 * - One-word names: first two letters (e.g., "John" → "JO")
 * - Empty/null: return "?"
 */
function getInitials(name) {
    if (!name || name.trim() === '') {
        return '?';
    }
    const trimmed = name.trim();
    const words = trimmed.split(/\s+/).filter((word) => word.length > 0);
    if (words.length >= 2) {
        // Two or more words: first letter of first two words
        const first = words[0];
        const second = words[1];
        if (first && second && first.length > 0 && second.length > 0) {
            return (first.charAt(0) + second.charAt(0)).toUpperCase();
        }
    }
    if (words.length === 1) {
        // Single word: first two letters (or first letter if only one char)
        const word = words[0];
        if (word && word.length > 0) {
            return word.length >= 2 ? word.slice(0, 2).toUpperCase() : word.charAt(0).toUpperCase();
        }
    }
    return '?';
}
/**
 * Avatar component using Radix Avatar primitive
 *
 * Displays a user avatar image with fallback initials.
 * Follows Protoss design system with semantic CSS variables.
 */
export const Avatar = React.forwardRef(({ src, name, alt, size = 'md', shape = 'circle', className, loading = 'eager', decoding = 'async', }, ref) => {
    const initials = getInitials(name);
    // Key on identity (name), not src. Avoids remounting the Radix Root
    // when src transitions null → URL - lets Radix handle the image swap
    // smoothly so the CSS crossfade animation can run.
    const avatarKey = name ?? '?';
    return (_jsxs(AvatarPrimitive.Root, { ref: ref, className: clsx('avatar', `avatar--${size}`, `avatar--${shape}`, className), children: [src && (_jsx(AvatarPrimitive.Image, { src: src, alt: alt ?? name ?? 'User avatar', className: "avatar__image", referrerPolicy: "no-referrer", loading: loading, decoding: decoding })), _jsx(AvatarPrimitive.Fallback, { className: "avatar__fallback", delayMs: 0, children: initials })] }, avatarKey));
});
Avatar.displayName = 'Avatar';
//# sourceMappingURL=Avatar.js.map